'use client';

import { usePortalContext } from '@cloai/uikit';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
  DropdownItem,
  Button,
} from '@heroui/react';
import { useTranslations } from 'next-intl';
import { FC, useState, useMemo, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { Gender } from '@/.gql/graphql';
import ArrowDown from '@/public/icons/arrow_drop_down.svg';
import ArrowUp from '@/public/icons/arrow_drop_up.svg';
import FaceIcon from '@/public/icons/face.svg';
import { AvatarItem } from '@/src/components/AvatarItem';
import {
  useStyleContext,
  useProfileContext,
  useGTMContext,
} from '@/src/providers';

const styles = {
  wrap: 'absolute top-0 w-12 left-0 flex gap-2 items-center',
  button: 'text-xs h-8 mt-2 font-medium bg-fill-menuBtn rounded-lg',
  buttonActive: 'border-yellow border-2',
};

export const AvatarsDropdown: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    setGenderMode,
    genderMode,
    setActiveAvatar,
    setIsYourPhoto,
    profileAvatars,
  } = useStyleContext();
  const profile = useProfileContext();
  const t = useTranslations('imageModal');
  const { currentPortal } = usePortalContext();
  const sendGTM = useGTMContext();

  const existingGenders = new Set(profileAvatars.map((a) => a.gender));
  const hasFemale = existingGenders.has(Gender.Female);
  const hasMale = existingGenders.has(Gender.Male);

  const avatarNodes = useMemo(
    () =>
      profileAvatars?.map((avatar) => (
        <DropdownItem
          key={avatar.id}
          textValue={avatar.name}
          className="hover:!bg-transparent hover:!shadow-none focus:!bg-transparent focus:!shadow-none !items-start"
        >
          <AvatarItem avatar={avatar} isHeader />
        </DropdownItem>
      )),
    [profileAvatars],
  );

  useEffect(() => {
    if (currentPortal !== PORTALS.GenerateImage) {
      setIsYourPhoto(false);
    }
  }, [currentPortal, setIsYourPhoto]);

  return (
    <div className={styles.wrap}>
      <Dropdown
        isOpen={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);

          if (open) {
            sendGTM({
              event: 'Gender',
              gender: genderMode,
            });
          }
        }}
      >
        <DropdownTrigger asChild textValue={'open'}>
          <Button
            variant="light"
            className="p-0 !min-w-12 hover:!bg-transparent hover:!shadow-none focus:!bg-transparent focus:!shadow-none"
            endContent={
              isOpen ? (
                <ArrowUp className="w-4 h-2" />
              ) : (
                <ArrowDown className="w-4 h-2" />
              )
            }
          >
            <FaceIcon className="w-5 h-5" />
          </Button>
        </DropdownTrigger>

        <DropdownMenu
          aria-label="Select avatar"
          className="max-w-[180px] p-3 bg-fill-menuBg rounded-lg"
          disableAnimation
        >
          {profileAvatars?.length > 0 ? (
            <DropdownSection
              title={t('avatarsTitle')}
              classNames={{
                heading: 'p-0 text-xs font-bold text-white',
                base: '!bg-transparent flex flex-col gap-2',
                group: '[&_li]:!px-0 flex flex-col gap-2',
              }}
            >
              {avatarNodes}
            </DropdownSection>
          ) : null}

          <DropdownSection
            classNames={{
              base: 'mb-0',
            }}
          >
            {!hasFemale ? (
              <DropdownItem
                key="female"
                onPress={() => {
                  setGenderMode(Gender.Female);
                  sendGTM({
                    event: 'Gender_choose',
                    gender: Gender.Female,
                  });
                  setActiveAvatar('');
                }}
                textValue={t('femaleFullName')}
                className={twMerge(
                  styles.button,
                  genderMode === Gender.Female && styles.buttonActive,
                )}
                startContent={
                  <span role="img" aria-label="Female">
                    👩
                  </span>
                }
              >
                {t('femaleFullName')}
              </DropdownItem>
            ) : null}
            {!hasMale ? (
              <DropdownItem
                key="male"
                textValue={t('maleFullName')}
                className={twMerge(
                  styles.button,
                  genderMode === Gender.Male && styles.buttonActive,
                )}
                onPress={() => {
                  setGenderMode(Gender.Male);
                  sendGTM({
                    event: 'Gender_choose',
                    gender: Gender.Male,
                  });
                  setActiveAvatar('');
                }}
                startContent={
                  <span role="img" aria-label="Male">
                    👨
                  </span>
                }
              >
                {t('maleFullName')}
              </DropdownItem>
            ) : null}
          </DropdownSection>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};
